import React, { useEffect } from 'react'

import {
  Box,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Controller, useFormContext } from 'react-hook-form'
import { isEmpty } from 'lodash'

import { Card, FileUploadInput } from 'components'
import {
  InternationalTransfers,
  SendContractAnalysisButton,
  Suboperators,
} from './components'

import useThirdPartyManagementStepper from 'hooks/useThirdPartyManagementStepper'
import useSnackbar from 'hooks/useSnackbar'

import constants from 'constants/index'
import helpers from 'helpers'
import * as service from 'service'

const ContractStep = () => {
  const { control, errors, watch, handleSubmit } = useFormContext()
  const {
    wantsToAdvanceStep,
    resetAdvanceStep,
    handleNext,
    setIsLoading,
    companySupplierId,
    isEdit,
    isShow,
    refresh,
  } = useThirdPartyManagementStepper()
  const snackbar = useSnackbar()

  const { OPTIONS_TYPES } = constants.dataTreatmentAgents
  const { COMPANY_AGENT_TYPES } = constants.companySuppliers
  const { STEPS } = constants.tours.thirdPartyManagements

  const onSubmit = async (data) => {
    try {
      setIsLoading(true)

      await service.dponet.suppliers.contract({
        companySupplierId,
        ...data,
        // O FormData omite arrays vazios, e o backend trata campo ausente como "não mexer".
        // O item em branco garante que a lista chegue vazia quando o usuário remove todos.
        suboperators: isEmpty(data.suboperators) ? [''] : data.suboperators,
        hireDate: helpers.thirdPartyManegementPartners.formatDate(
          data.hireDate,
        ),
        renewDate: helpers.thirdPartyManegementPartners.formatDate(
          data.renewDate,
        ),
      })

      if (isEdit) {
        refresh()

        snackbar.open({
          message: 'Etapa de Contrato atualizada com sucesso',
          variant: 'success',
        })
      }

      resetAdvanceStep()
      handleNext()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao criar fornecedor',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
      resetAdvanceStep()
    }
  }

  useEffect(() => {
    if (!wantsToAdvanceStep || isShow) return

    let validationPassed = false

    // Para não travar o formulário se der erro de validação do yup
    handleSubmit((data) => {
      validationPassed = true
      return onSubmit(data)
    })().then(() => {
      if (!validationPassed) resetAdvanceStep()
    })
  }, [wantsToAdvanceStep])

  return (
    <Box display="flex" flexDirection="column" gridGap={24}>
      <Card
        id={STEPS.CREATION.CONTRACT[1]}
        title="Contrato"
        actionButton={<SendContractAnalysisButton />}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Controller
              as={
                <KeyboardDatePicker
                  fullWidth
                  format="DD/MM/yyyy"
                  label="Data da Contratação"
                  helperText={errors?.hireDate?.message}
                  error={!!errors?.hireDate}
                  invalidDateMessage="Data inválida"
                  disabled={isShow}
                />
              }
              defaultValue={watch('hireDate') || null}
              control={control}
              name="hireDate"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              as={
                <KeyboardDatePicker
                  disablePast
                  fullWidth
                  format="DD/MM/yyyy"
                  label="Data da Renovação"
                  helperText={errors?.renewDate?.message}
                  error={!!errors?.renewDate}
                  invalidDateMessage="Data inválida"
                  disabled={isShow}
                />
              }
              defaultValue={watch('renewDate') || null}
              control={control}
              name="renewDate"
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="subtitle2">
              Qual o papel da sua organização?
            </Typography>
            <Controller
              as={
                <RadioGroup>
                  {COMPANY_AGENT_TYPES.map((option) => (
                    <FormControlLabel
                      key={option.id}
                      value={option.id.toLowerCase()}
                      control={<Radio disabled={isShow} />}
                      label={option.name}
                    />
                  ))}
                </RadioGroup>
              }
              control={control}
              defaultValue=""
              name="companyAgentType"
            />
            {errors?.companyAgentType && (
              <FormHelperText error>
                {errors.companyAgentType.message}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">
              Qual o papel do fornecedor?
            </Typography>
            <Controller
              as={
                <RadioGroup>
                  {OPTIONS_TYPES.map((option) => (
                    <FormControlLabel
                      key={option.id}
                      value={option.id.toLowerCase()}
                      control={<Radio disabled={isShow} />}
                      label={option.name}
                    />
                  ))}
                </RadioGroup>
              }
              control={control}
              defaultValue=""
              name="treatmentAgentType"
            />
            {errors?.treatmentAgentType && (
              <FormHelperText error>
                {errors.treatmentAgentType.message}
              </FormHelperText>
            )}
          </Grid>

          <Grid item xs={12}>
            <Suboperators />
          </Grid>

          <Grid item xs={12}>
            <InternationalTransfers />
          </Grid>
        </Grid>
      </Card>

      <Card id={STEPS.CREATION.CONTRACT[2]} title="Documentos">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FileUploadInput
              controlName="attachments"
              accept={constants.validations.PDF_FILE_TYPE}
              hasDownloadFile
              controllerBar
              multiple
              blockUnaccepted
              disabled={isShow}
            />
          </Grid>
        </Grid>
      </Card>
    </Box>
  )
}

export default ContractStep
